// Family map in the client card (owner-approved idea 33): spouse, children, parents, grandparents, godparents — people of
// the book or just names — and who pays for whom. It joins three sources so nothing is typed twice:
//   household   the members of the client's household (schema 1)
//   links       family_links (schema 12): a relative of the client, another person of the book or only a name, with a
//               birth date, a baptism date (for the anniversary) and «who pays for whom»
//   reverse     a link recorded on the relative's card is shown on this card too, with the inverse relation
// Policies tell the rest: a policy the client owns that insures a relative means the client pays for that cover.
import type { Changeset, WorkspaceView } from './changeset.ts';
import { DomainError, clean, created, opt, put, requirePerson, timeline, touched, type Ctx } from './cmd.ts';
import { addDays, ageOn, diffDays, normalizeDay, type Day } from './dates.ts';
import { householdOf } from './household.ts';
import { coverageForPerson, heldInForce } from './person-cover.ts';
import { FAMILY_PAYS, FAMILY_RELATIONS, type FamilyLink, type FamilyPays, type FamilyRelation } from './schema.ts';

export const INVERSE_RELATION: Record<FamilyRelation, FamilyRelation> = {
  spouse: 'spouse', partner: 'partner', child: 'parent', parent: 'child', grandparent: 'grandchild', grandchild: 'grandparent',
  sibling: 'sibling', godparent: 'godchild', godchild: 'godparent', other: 'other',
};
const INVERSE_PAYS: Record<FamilyPays, FamilyPays> = { none: 'none', pays_for: 'paid_by', paid_by: 'pays_for' };

export interface FamilyLinkInput {
  id?: string; personId: string; relation: FamilyRelation; relatedPersonId?: string | null; name?: string | null;
  birthDate?: string | null; baptismDate?: string | null; pays?: FamilyPays | null; notes?: string | null;
}
export function saveFamilyLink(v: WorkspaceView, input: FamilyLinkInput, ctx: Ctx): Changeset {
  const person = requirePerson(v, input.personId);
  const cur = input.id ? v.get('family_links', input.id) : undefined;
  if (input.id && (!cur || cur.personId !== person.id)) throw new DomainError('family.not_found');
  if (!(FAMILY_RELATIONS as readonly string[]).includes(input.relation)) throw new DomainError('family.relation');
  const related = input.relatedPersonId ? v.get('persons', input.relatedPersonId) : undefined;
  if (input.relatedPersonId && !related) throw new DomainError('person.not_found');
  if (related && related.id === person.id) throw new DomainError('family.self');
  const name = opt(input.name) ?? related?.displayName ?? null;
  if (!name) throw new DomainError('family.name_required');
  const birthDate = input.birthDate ? normalizeDay(input.birthDate) : null;
  if (input.birthDate && !birthDate) throw new DomainError('family.date');
  if (birthDate && birthDate > ctx.today) throw new DomainError('family.birth_future');
  const baptismDate = input.baptismDate ? normalizeDay(input.baptismDate) : null;
  if (input.baptismDate && !baptismDate) throw new DomainError('family.date');
  const pays = input.pays ?? 'none';
  if (!(FAMILY_PAYS as readonly string[]).includes(pays)) throw new DomainError('family.pays');
  if (related && !cur && v.where('family_links', 'personId', person.id).some((l) => l.relatedPersonId === related.id)) throw new DomainError('family.duplicate');
  const fields = { personId: person.id, relation: input.relation, relatedPersonId: related?.id ?? null, name, birthDate, baptismDate, pays, notes: opt(input.notes) };
  const row: FamilyLink = cur ? touched(ctx, cur, fields) : created<'family_links'>(ctx, fields);
  const puts = [put('family_links', row)];
  if (!cur) puts.push(timeline(ctx, person.id, 'system', 'timeline.family_added', `${name}`, { table: 'family_links', id: row.id }));
  return { action: cur ? 'family.update' : 'family.add', entity: 'family_links', entityId: row.id, summary: `${person.displayName} · ${name}`, puts, deletes: [] };
}
export function deleteFamilyLink(v: WorkspaceView, id: string): Changeset {
  const l = v.get('family_links', id);
  if (!l) throw new DomainError('family.not_found');
  return { action: 'family.delete', entity: 'family_links', entityId: id, summary: l.name ?? '', puts: [], deletes: [{ table: 'family_links', id }] };
}

export interface FamilyMember {
  key: string; source: 'household' | 'link' | 'reverse'; linkId: string | null; personId: string | null; name: string;
  relation: FamilyRelation; birthDate: Day | null; baptismDate: Day | null; age: number | null; pays: FamilyPays;
  /** active policies on this relative (as insured or holder) and whether the client pays for one of them */
  policies: number; clientPaysFor: boolean; isClient: boolean; notes: string | null;
}
const HOUSEHOLD_RELATION: Record<string, FamilyRelation> = { spouse: 'spouse', child: 'child', dependent: 'other', other: 'other' };

/** the client's family: household members, recorded links and links recorded on the relatives' cards */
export function familyMap(v: WorkspaceView, personId: string, today: Day, opts: { policies?: boolean } = {}): FamilyMember[] {
  const withPolicies = opts.policies !== false;
  const me = v.get('persons', personId);
  if (!me) return [];
  const out = new Map<string, FamilyMember>();
  // policies in force today that insure the relative (any insured party, coverageForPerson) or that they hold
  const polsOf = (pid: string) => new Set([...coverageForPerson(v, pid, today).map((c) => c.policy.id), ...heldInForce(v, pid, today).map((p) => p.id)]);
  // the client holds a policy in force that insures this relative
  const paysFor = (pid: string) => coverageForPerson(v, pid, today).some((c) => c.policy.personId === me.id);
  const add = (m: Omit<FamilyMember, 'age' | 'policies' | 'clientPaysFor' | 'isClient'>) => {
    const k = m.personId ?? `name:${m.linkId}`;
    if (out.has(k) && m.source === 'household') return;
    const person = m.personId ? v.get('persons', m.personId) : undefined;
    const birth = m.birthDate ?? person?.birthDate ?? null;
    const prev = out.get(k);
    out.set(k, {
      ...m, key: k, birthDate: birth, age: birth ? ageOn(birth, today) : null,
      // a link says more than the household (relation, baptism, pays): it wins when both name the same person
      baptismDate: m.baptismDate ?? prev?.baptismDate ?? null, notes: m.notes ?? prev?.notes ?? null,
      policies: withPolicies && m.personId ? polsOf(m.personId).size : 0, clientPaysFor: withPolicies && m.personId ? paysFor(m.personId) : false, isClient: person?.stage === 'client',
    });
  };
  for (const h of householdOf(v, me.id)) if (h.person.id !== me.id) add({ key: '', source: 'household', linkId: null, personId: h.person.id, name: h.person.displayName, relation: HOUSEHOLD_RELATION[h.role] ?? 'other', birthDate: h.person.birthDate ?? null, baptismDate: null, pays: 'none', notes: null });
  for (const l of v.where('family_links', 'personId', me.id)) add({ key: '', source: 'link', linkId: l.id, personId: l.relatedPersonId ?? null, name: l.relatedPersonId ? v.get('persons', l.relatedPersonId)?.displayName ?? l.name ?? '—' : l.name ?? '—', relation: l.relation, birthDate: l.birthDate ?? null, baptismDate: l.baptismDate ?? null, pays: l.pays ?? 'none', notes: l.notes ?? null });
  for (const l of v.where('family_links', 'relatedPersonId', me.id)) {
    const other = v.get('persons', l.personId);
    if (!other || out.has(other.id)) continue;
    add({ key: '', source: 'reverse', linkId: l.id, personId: other.id, name: other.displayName, relation: INVERSE_RELATION[l.relation], birthDate: other.birthDate ?? null, baptismDate: null, pays: INVERSE_PAYS[l.pays ?? 'none'], notes: null });
  }
  const order: FamilyRelation[] = ['spouse', 'partner', 'child', 'grandchild', 'godchild', 'parent', 'grandparent', 'godparent', 'sibling', 'other'];
  return [...out.values()].sort((a, b) => order.indexOf(a.relation) - order.indexOf(b.relation) || (a.birthDate ?? '9').localeCompare(b.birthDate ?? '9') || a.name.localeCompare(b.name));
}

/** the next yearly date of a day on or after `from` (29 Feb → 28 Feb in other years) */
export function nextOccurrence(date: Day, from: Day): Day {
  const md = date.slice(5);
  const mk = (y: number) => { const d = `${y}-${md}`; return md === '02-29' && !(y % 4 === 0 && (y % 100 !== 0 || y % 400 === 0)) ? `${y}-02-28` : d; };
  const y = Number(from.slice(0, 4));
  const thisYear = mk(y);
  return thisYear >= from ? thisYear : mk(y + 1);
}

export interface FamilyReminder { kind: 'turns18' | 'baptism'; personId: string; relativeId: string | null; name: string; date: Day; days: number; years: number }
/** children turning 18 and baptism anniversaries in the next `days` days, for every client's family */
export function familyReminders(v: WorkspaceView, today: Day, days = 60): FamilyReminder[] {
  const out: FamilyReminder[] = [];
  const end = addDays(today, days);
  const seen = new Set<string>();
  const parents = new Set<string>();
  for (const l of v.rows('family_links')) parents.add(l.personId);
  for (const p of v.rows('persons')) if (p.householdId && !p.archivedAt && p.stage !== 'dependent') parents.add(p.id);
  for (const pid of parents) {
    const owner = v.get('persons', pid);
    if (!owner || owner.archivedAt) continue;
    for (const m of familyMap(v, pid, today, { policies: false })) {
      if (m.birthDate && (m.relation === 'child' || m.relation === 'godchild' || m.relation === 'grandchild')) {
        const d18 = `${Number(m.birthDate.slice(0, 4)) + 18}${m.birthDate.slice(4)}`;
        const k = `18:${m.personId ?? m.key}`; // once per child, even when both parents are clients
        if (d18 >= today && d18 <= end && !seen.has(k)) { seen.add(k); out.push({ kind: 'turns18', personId: pid, relativeId: m.personId, name: m.name, date: d18, days: diffDays(today, d18), years: 18 }); }
      }
      if (m.baptismDate && m.baptismDate <= end) {
        const next = nextOccurrence(m.baptismDate, today);
        const years = Number(next.slice(0, 4)) - Number(m.baptismDate.slice(0, 4));
        const kb = `b:${m.personId ?? m.key}`;
        if (years >= 1 && next <= end && !seen.has(kb)) { seen.add(kb); out.push({ kind: 'baptism', personId: pid, relativeId: m.personId, name: m.name, date: next, days: diffDays(today, next), years }); }
      }
    }
  }
  return out.sort((a, b) => a.date.localeCompare(b.date));
}
export const familyRelationOk = (x: unknown): x is FamilyRelation => (FAMILY_RELATIONS as readonly string[]).includes(String(x));
export const familyNameOf = (l: Pick<FamilyLink, 'name'>) => clean(l.name);
